import { Fragment, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import schoolsApi from '../api/universities';
import validateEmailApi from '../api/validateEmail';
import cleanUpForm from '../services/services';
import SignUpContent from '../components/SignUpContent';
import FormButtons from '../components/FormButtons';
import history from '../history';
import { serverLink } from '../constants/serverlinks';
import SignUpContainer from '../components/SignUpContainer';
import { formFlowCopy } from '../copy/formCopy';
import { formValidationSchema } from '../schemas/formValidationSchema';

const SignUp = () => {
  const isFirstRun = useRef(true);
  const [timeoutId, setTimeoutId] = useState(null);
  const [formData, setFormData] = useState({ newsletter: true });
  const [errorData, setErrorData] = useState({});
  const [currentForm, setCurrentForm] = useState(null);
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentInputs, setCurrentInputs] = useState([]);
  const [currentButtonLabel, setCurrentButtonLabel] = useState('');
  const [currentButtonCopy, setCurrentButtonCopy] = useState([]);
  const [currentButtonHint, setCurrentButtonHint] = useState([]);
  const [showStepButtons, setShowStepButtons] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [schoolList, setSchoolList] = useState([]);
  const [filteredSchoolList, setFilteredSchoolList] = useState([]);
  const [filtering, setFiltering] = useState(false);
  const [schoolListVisible, setSchoolListVisible] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSignUpSuccessful, setIsSignUpSuccessful] = useState(false);

  const validateInput = () => {
    const { error } = formValidationSchema.validate(formData, {
      abortEarly: false,
    });

    const errorMessages = Object.keys(formData).reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {});

    if (error) {
      error.details.forEach((detail) => {
        errorMessages[detail.path[0]] = detail.message;
      });
    }

    const requiredInputs =
      currentForm.inputs &&
      currentForm.inputs
        .filter((input) => input.required === true)
        .map((input) => input.name);

    if (currentButtonCopy[0].type === 'companyInfo') {
      requiredInputs.push('otherIndustry');
    }

    const errorsMatchRequiredInputs =
      requiredInputs &&
      requiredInputs.filter(
        (item) => !Object.keys(errorMessages).includes(item)
      );

    const errorsHaveValues = Object.values(errorMessages).some(
      (value) => value !== ''
    );
    setErrorData(errorMessages);
    setIsFormValid(
      errorsMatchRequiredInputs &&
        errorsMatchRequiredInputs.length === 0 &&
        !errorsHaveValues
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const emailExists = name === 'email' && checkEmailAvailability(value);
    if (!emailExists) {
      setFormData({ ...formData, [name]: value });
    }
    if (timeoutId) clearTimeout(timeoutId);

    if (name === 'university_name' && value.length > 0) {
      setFiltering(true);
      const typingTimeout = setTimeout(() => {
        filterSchoolList(value);
      }, 500);

      setTimeoutId(typingTimeout);
    } else if (name === 'university_name' && value.length === 0) {
      setFiltering(false);
      setSchoolListVisible(false);
    }

    if (name === 'newsletter') {
      if (value === 'true') {
        setFormData({
          ...formData,
          newsletter: true,
        });
      } else {
        setFormData({
          ...formData,
          newsletter: false,
        });
      }
    }

    if (name === 'universityRole') {
      if (value === 'true') {
        setFormData({
          ...formData,
          is_faculty: true,
          is_university_staff: false,
          universityRole: true,
        });
      } else {
        setFormData({
          ...formData,
          is_faculty: false,
          is_university_staff: true,
          universityRole: true,
        });
      }
    }
  };

  const onFormClick = (e) => {
    e.preventDefault();
    const actionType = e.target.getAttribute('type');

    if (actionType === 'login') {
      history.push('/');
    }

    if (actionType === 'universityFlow') {
      setFormData({ ...formData, is_university: true });
      // Skip NASPAA question — go directly to university info
      setCurrentForm(formFlowCopy['universityInfo']);
      setErrorData({});
      return;
    } else if (actionType === 'companyFlow') {
      setFormData({ ...formData, is_university: false });
      setShowStepButtons(false);
    } else if (actionType === 'multiButton') {
      setFormData({ ...formData, industry: e.target.innerText });
    } else if (actionType === 'companyInfo') {
      setShowStepButtons(true);
      if (e.target.innerText === 'Other') {
        setFormData({
          ...formData,
          industry: e.target.innerText,
        });
        setShowStepButtons(true);
      } else if (e.target.innerText === 'Next') {
        setShowStepButtons(true);
      } else {
        setFormData({ ...formData, industry: e.target.innerText });
        setShowStepButtons(true);
      }
    }
    if (
      (actionType && e.target.innerText !== 'Other') ||
      e.target.innerText === 'undefined'
    ) {
      setCurrentForm(formFlowCopy[actionType]);
    }

    setErrorData({});
  };

  const checkEmailAvailability = (email) => {
    validateEmailApi
      .get('/validate-email', {
        params: {
          email,
        },
      })
      .then((res) => {
        const { data } = res;

        if (data.success === true) {
          setErrorData({
            ...errorData,
            email: `There's already an account with this email address. <span class="text-dms-primary cursor-pointer hover:underline font-semibold"><a href="https://sims.dmsi-sims.com/forgot_password" target="_blank" rel="noreferrer noopener">Forgot your password?</a></span>`,
          });
          return true;
        } else {
          return false;
        }
      });
  };

  const onSignUp = (e) => {
    e.preventDefault();
    const cleanedFormData = cleanUpForm(formData);

    axios
      .post(`${serverLink}/api/auth/register`, {
        cleanedFormData,
      })
      .then((res) => {
        if (res.data.success) {
          setIsSignUpSuccessful(true);
          setCurrentForm(formFlowCopy['orgSuccess']);
          setShowStepButtons(false);
        } else {
          setIsSignUpSuccessful(false);
          //create invalid form error feedback step
          console.log('Unsuccessful: ', res.data);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const closeSignUp = () => {
    history.push('/');
  };

  const selectSchoolFromList = (e) => {
    const university = e.target.getAttribute('id');
    const value = e.target.innerText;

    schoolsApi
      .get(`/universities/${university}/`)
      .then((res) => {
        const { data } = res;
        setFormData({
          ...formData,
          valid_univ_domain: data.domains,
          university_name: value,
        });
      })
      .catch((err) => console.log(err));

    setSchoolListVisible(false);
    setFiltering(false);
  };

  const fetchSchoolList = () => {
    schoolsApi
      .get('/university_names/')
      .then((res) => {
        const { data } = res;
        setSchoolList(data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const filterSchoolList = (university_name) => {
    if (university_name.length > 0) {
      const valueMatch = Array.from(university_name).reduce(
        (a, v, i) => `${a}[^${university_name.substr(i)}]*?${v}`,
        ''
      );
      const valueRegEx = RegExp(valueMatch, 'i');
      let filteredList = schoolList
        .filter((school) => {
          return school.name.match(valueRegEx);
        })
        .sort();
      setFilteredSchoolList(filteredList);
      setSchoolListVisible(true);
      setFiltering(false);
    }
  };

  useEffect(() => {
    fetchSchoolList();
    setCurrentForm(formFlowCopy.accountInfo);
  }, []);

  useEffect(() => {
    if (currentForm) {
      setCurrentTitle(currentForm.title);
      setCurrentInputs(currentForm.inputs);
      setCurrentButtonLabel(currentForm.buttonLabel);
      setCurrentButtonCopy(currentForm.buttonCopy);
      setCurrentButtonHint(currentForm.buttonHint || null);
      setCurrentStep(currentForm.formStep);
      setIsFormValid(false);
    }
  }, [currentForm]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    validateInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return (
    <Fragment>
      <SignUpContainer
        closeSignUp={closeSignUp}
        onFormClick={(e) => onFormClick(e)}
      >
        <div className=''>
          <SignUpContent
            onChange={(e, type) => handleChange(e, type)}
            selectSchoolFromList={(e) => selectSchoolFromList(e)}
            handleChange={(e) => handleChange(e)}
            onFormClick={(e) => onFormClick(e)}
            formData={formData}
            currentForm={currentForm}
            currentTitle={currentTitle}
            currentInputs={currentInputs}
            currentStep={currentStep}
            errorData={errorData}
            filteredSchoolList={filteredSchoolList}
            schoolListVisible={schoolListVisible}
            filtering={filtering}
          />
          {showStepButtons && (
            <div className='flex flex-col lg:flex-row lg:space-x-5 space-y-2 lg:justify-between z-0'>
              <FormButtons
                onSignUp={(e) => onSignUp(e)}
                onCloseSignUp={() => closeSignUp()}
                onFormClick={(e) => onFormClick(e)}
                buttonLabel={currentButtonLabel}
                buttonCopy={currentButtonCopy}
                buttonHint={currentButtonHint}
                errorData={errorData}
                formData={formData}
                isFormValid={isFormValid}
                isSignUpSuccessful={isSignUpSuccessful}
              />
            </div>
          )}
        </div>
      </SignUpContainer>
    </Fragment>
  );
};

export default SignUp;
